/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';

// @fullcalendar components
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import thLocale from '@fullcalendar/core/locales/th';

// @mui material components
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Custom styles for Calendar
import CalendarRoot from 'examples/Calendar/CalendarRoot';

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from 'context';

function EventCalendar({ header, ...rest }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const validClassNames = [
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
    'light',
    'dark',
  ];

  const events = rest.events
    ? rest.events.map((el) => ({
        ...el,
        className: validClassNames.find((item) => item === el.className)
          ? `event-${el.className}`
          : 'event-info',
      }))
    : [];

  return (
    <Card sx={{ height: '100%' }}>
      <MDBox pt={header.title || header.date ? 2 : 0} px={2} lineHeight={1}>
        {header.title ? (
          <MDTypography variant='h6' fontWeight='medium' textTransform='capitalize'>
            {header.title}
          </MDTypography>
        ) : null}
        {header.date ? (
          <MDTypography component='p' variant='button' color='text' fontWeight='regular'>
            {header.date}
          </MDTypography>
        ) : null}
      </MDBox>
      <CalendarRoot p={2} ownerState={{ darkMode }}>
        <FullCalendar
          {...rest}
          locale={thLocale}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          events={events}
          height='100%'
        />
      </CalendarRoot>
    </Card>
  );
}

// Setting default values for the props of Calendar
EventCalendar.defaultProps = {
  header: {
    title: '',
    date: '',
  },
};

// Typechecking props for the Calendar
EventCalendar.propTypes = {
  header: PropTypes.shape({
    title: PropTypes.string,
    date: PropTypes.string,
  }),
};

export default EventCalendar;
